import React, { useState, useMemo, useCallback } from 'react'
import { useWatch, useFormContext } from 'react-hook-form'
import { useBoolean } from 'react-use'

import { getResourceNameFromResponseBody } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { EndpointResponse } from 'types/api/status-checks/status-check'
import { isValidUrl } from 'utils/strings'

export interface StatusCheckDetailsContextValues {
  endpointResponse: EndpointResponse | undefined
  handleSuccess: (endpointResponse: EndpointResponse) => Promise<void>
  isStatusCheckConnectionValidated: boolean
  isStatusCheckEvaluationValidated: boolean
  isTestSuccessEvaluationDisabled: boolean
  serviceName?: string
  setIsStatusCheckConnectionValidated: (arg: boolean) => void
  setIsStatusCheckEvaluationValidated: (arg: boolean) => void
  setEndpointResponse: (endpointResponse: EndpointResponse | undefined) => void
  shouldDisplayPredicateConfiguration: boolean
  handleTestConnectionError: () => void
  isTestStatusCheckDisabled: boolean
}

export const StatusCheckDetailsContext = React.createContext<
  StatusCheckDetailsContextValues | undefined
>(undefined)

const fieldsToWatch = [
  'monitorUrl',
  'externalIntegration',
  'evaluationConfiguration.responseBodyEvaluation.predicates',
  'statusCheckName'
] as const

export const StatusCheckDetailsProvider: React.FC<{
  serviceName?: string
}> = ({ children, serviceName = '' }) => {
  const [
    isStatusCheckEvaluationValidated,
    setIsStatusCheckEvaluationValidated
  ] = useBoolean(false)

  const [
    isStatusCheckConnectionValidated,
    setIsStatusCheckConnectionValidated
  ] = useBoolean(false)

  const [endpointResponse, setEndpointResponse] = useState<
    EndpointResponse | undefined
  >()

  const { control, formState, setValue, trigger } =
    useFormContext<StatusCheckFormValues>()

  const [monitorUrl, externalIntegration, predicates, statusCheckName] =
    useWatch<StatusCheckFormValues, typeof fieldsToWatch>({
      control,
      name: fieldsToWatch
    })

  const isTestStatusCheckDisabled =
    Boolean(formState.errors.monitorUrl) || !isValidUrl(monitorUrl)

  const isTestSuccessEvaluationDisabled =
    isTestStatusCheckDisabled ||
    !isStatusCheckConnectionValidated ||
    !endpointResponse ||
    Boolean(formState.errors?.requestTimeout)

  const shouldDisplayPredicateConfiguration = useMemo<boolean>(() => {
    if (predicates && !endpointResponse) {
      return predicates.length > 0
    }

    const { contentType } = endpointResponse ?? {}
    // We can probably assume here that we lead with '/', but cannot guarantee that this is
    // always placed at the end of the string. Example: PNI responses might be semi-colon delimited.
    const isJsonPattern = /\/json/i
    const isJsonContentType = contentType
      ? isJsonPattern.test(contentType)
      : false

    return isJsonContentType
  }, [endpointResponse, predicates])

  const handleSuccess = useCallback(
    async (endpointResponse: EndpointResponse) => {
      setEndpointResponse(endpointResponse)
      setIsStatusCheckConnectionValidated(true)
      setIsStatusCheckEvaluationValidated(false)

      // Pull the resource name from the response body
      const resourceName = externalIntegration
        ? getResourceNameFromResponseBody(endpointResponse, externalIntegration)
        : undefined

      // don't overwrite the status check name from the response body if it exists
      // in the form
      if (resourceName && !statusCheckName) {
        setValue('statusCheckName', resourceName)
      }

      // Validate monitorUrl on test success
      await trigger('monitorUrl')
    },
    [
      externalIntegration,
      statusCheckName,
      setIsStatusCheckConnectionValidated,
      setIsStatusCheckEvaluationValidated,
      setValue,
      trigger
    ]
  )

  const handleTestConnectionError = useCallback(() => {
    if (isStatusCheckConnectionValidated) {
      setIsStatusCheckConnectionValidated(false)
    }
  }, [isStatusCheckConnectionValidated, setIsStatusCheckConnectionValidated])

  const value = useMemo(() => {
    return {
      endpointResponse,
      handleSuccess,
      handleTestConnectionError,
      isStatusCheckConnectionValidated,
      isStatusCheckEvaluationValidated,
      isTestStatusCheckDisabled,
      isTestSuccessEvaluationDisabled,
      serviceName,
      setEndpointResponse,
      setIsStatusCheckConnectionValidated,
      setIsStatusCheckEvaluationValidated,
      shouldDisplayPredicateConfiguration
    }
  }, [
    endpointResponse,
    handleSuccess,
    handleTestConnectionError,
    isStatusCheckConnectionValidated,
    isStatusCheckEvaluationValidated,
    isTestStatusCheckDisabled,
    isTestSuccessEvaluationDisabled,
    serviceName,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated,
    shouldDisplayPredicateConfiguration
  ])

  return (
    <StatusCheckDetailsContext.Provider value={value}>
      {children}
    </StatusCheckDetailsContext.Provider>
  )
}

export function useStatusCheckDetailsContext() {
  const context = React.useContext(StatusCheckDetailsContext)

  if (context === undefined) {
    throw new Error(
      'useStatusCheckDetailsContext must be used within a StatusCheckDetailsProvider'
    )
  }

  return context
}
